import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { isoMondayString } from "../constants";
import { useConversations, type AiAction, type ChatMsg } from "../hooks/useConversations";
import { useSpeech, type SpeechLang } from "../hooks/useSpeech";

interface AiResult {
  reply: string;
  actions: AiAction[];
  ok: boolean;
}

interface Balance {
  available: boolean;
  currency?: string;
  total?: number;
  granted?: number;
  topped_up?: number;
  error?: string;
}

const WRITE_TOOLS = new Set([
  "create_recipe",
  "update_recipe",
  "delete_recipe",
  "set_recipe_image",
  "delete_recipe_image",
  "assign_recipe_to_slot",
  "assign_recipes",
  "unassign_card",
  "add_shopping_items",
  "remove_shopping_items",
  "apply_ingredient_suggestions",
  "set_ingredient_image",
  "update_ingredient",
  "create_ingredient",
  "delete_ingredient",
]);

const TOOL_LABELS: Record<string, string> = {
  search_recipes: "Cerca de receptes",
  get_recipe: "Consulta de recepta",
  create_recipe: "Recepta creada",
  update_recipe: "Recepta modificada",
  delete_recipe: "Recepta eliminada",
  search_recipe_images: "Cerca foto recepta",
  set_recipe_image: "Foto recepta actualitzada",
  delete_recipe_image: "Foto recepta esborrada",
  list_week: "Consulta de la setmana",
  assign_recipe_to_slot: "Recepta assignada",
  assign_recipes: "Receptes assignades",
  unassign_card: "Recepta desassignada",
  list_users: "Consulta d'usuaris",
  list_shopping: "Consulta de la compra",
  add_shopping_items: "Compra: articles afegits",
  remove_shopping_items: "Compra: articles esborrats",
  search_ingredients: "Cerca d'ingredients",
  get_ingredient: "Consulta d'ingredient",
  update_ingredient: "Ingredient modificat",
  create_ingredient: "Ingredient creat",
  delete_ingredient: "Ingredient esborrat",
  improve_ingredients: "Millores suggerides",
  apply_ingredient_suggestions: "Millores aplicades",
  search_ingredient_image: "Cerca foto ingredient",
  set_ingredient_image: "Foto ingredient actualitzada",
};

const SUGGESTIONS = [
  "Omple el menú de la setmana",
  "Posa foto a les receptes que no en tenen",
  "Afegeix els ingredients del sopar a la compra",
  "Crea l'ingredient 'Moniato' amb 86 kcal, 20g carbohidrats i 1.6g proteïna per 100g",
  "Cerca l'ingredient tomàquet i posa-li la unitat per defecte 'g' i el grup NOVA 1",
  "Quins ingredients no tenen foto? Posa'ls-en una",
];

const LANG_KEY = "cookmenu:ai-lang";

function loadLang(): SpeechLang {
  const v = (typeof localStorage !== "undefined" && localStorage.getItem(LANG_KEY)) || "";
  return v === "es-ES" ? "es-ES" : "ca-ES";
}

export function AiAssistant({ onClose }: { onClose: () => void }) {
  const { messages, setMessages, conversations, active, startNew, selectConversation, deleteConversation } =
    useConversations();
  const [lang, setLang] = useState<SpeechLang>(loadLang);
  const speech = useSpeech(lang);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Espai que ocupa el teclat virtual (mòbil). 0 si no n'hi ha (sempre al PC).
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
  }, [lang]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      setKeyboardInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(() => {
    api.get<Balance>("/ai/balance").then(setBalance).catch(() => null);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  function send(text: string, viaVoice = false) {
    const prompt = text.trim();
    if (!prompt || loading) return;
    if (speech.listening) speech.stopListening();
    setInput("");
    // Historial: tots els missatges actuals (abans d'afegir aquest)
    const history = messages
      .filter((m) => m.role !== "error")
      .map((m) => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
    // L'usuari va a l'índex actual; la resposta de l'IA, just després.
    const replyIndex = messages.length + 1;
    setMessages((prev) => [...prev, { role: "user", text: prompt }]);
    doSend(prompt, history, viaVoice, replyIndex);
  }

  // Ref a la versió més recent de `send` perquè el callback del micròfon
  // (que s'executa de manera asíncrona en acabar de parlar) faci servir
  // sempre l'historial i l'índex actuals.
  const sendRef = useRef(send);
  useEffect(() => {
    sendRef.current = send;
  });

  async function doSend(
    prompt: string,
    history: { role: string; content: string }[],
    viaVoice: boolean,
    replyIndex: number
  ) {
    setLoading(true);
    try {
      const res = await api.post<AiResult>("/ai/command", {
        prompt,
        weekDate: isoMondayString(),
        lang,
        history,
      });
      setMessages((m) => [
        ...m,
        { role: res.ok ? "assistant" : "error", text: res.reply, actions: res.actions },
      ]);
      // Si la pregunta es va fer per veu, llegim la resposta en veu alta.
      // Si es va escriure, no.
      if (viaVoice && res.ok && speech.synthSupported) {
        speech.speak(res.reply, String(replyIndex));
      }
      if (res.actions.some((a) => WRITE_TOOLS.has(a.tool) && !("error" in a.result))) {
        window.dispatchEvent(new CustomEvent("cookmenu:data-changed"));
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Error desconegut";
      setMessages((m) => [...m, { role: "error", text: msg }]);
    } finally {
      setLoading(false);
    }
  }

  function handleNewConversation() {
    speech.stopSpeaking();
    if (speech.listening) speech.stopListening();
    setShowHistory(false);
    setInput("");
    startNew();
  }

  function toggleMic() {
    if (speech.listening) {
      speech.stopListening();
    } else {
      speech.startListening((text, isFinal) => {
        setInput(text);
        // En acabar de parlar, enviem l'ordre sola (marcada com a veu).
        if (isFinal && text.trim()) sendRef.current(text, true);
      });
    }
  }

  return (
    <>
      <div className="ai-scrim" onClick={onClose} />
      {/* Mòbil: full inferior a tota l'amplada. PC: panell ancorat a baix-dreta. */}
      <div
        className={`aip${maximized ? " max" : ""}`}
        role="dialog"
        aria-label="Assistent NaraMenu"
        style={keyboardInset > 0 ? { top: 12, bottom: keyboardInset, height: "auto" } : undefined}
      >
        {/* Header */}
        <div className="aip-head">
          <div className="ttl" title={active?.title}>
            <span className="sp">
              <SparkleIcon size={17} />
            </span>
            <b>{active ? active.title : "Assistent NaraMenu"}</b>
          </div>
          <button type="button" className="aip-ib" title="Nova conversa" aria-label="Nova conversa" onClick={handleNewConversation}>
            <PlusIcon />
          </button>
          <button
            type="button"
            className={`aip-ib ${showHistory ? "on" : ""}`}
            title="Converses recents"
            aria-label="Converses recents"
            onClick={() => setShowHistory((v) => !v)}
          >
            <HistoryIcon />
          </button>
          {balance && <BalanceRing balance={balance} />}
          <button
            type="button"
            className="aip-ib aip-maxtoggle"
            title={maximized ? "Restaura la mida" : "Maximitza"}
            aria-label={maximized ? "Restaura la mida" : "Maximitza"}
            aria-pressed={maximized}
            onClick={() => setMaximized((v) => !v)}
          >
            {maximized ? <RestoreIcon /> : <MaximizeIcon />}
          </button>
          <button type="button" className="aip-ib" title="Tanca" aria-label="Tanca" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Històric de converses (desplegable) */}
        {showHistory && (
          <>
            <div style={{ position: "absolute", inset: 0, zIndex: 7 }} onClick={() => setShowHistory(false)} />
            <div className="hist">
              <div className="h5">Converses recents</div>
              {conversations.length === 0 ? (
                <div className="hist-empty">Encara no hi ha converses desades.</div>
              ) : (
                conversations.map((c) => (
                  <div
                    key={c.id}
                    className={`hist-row ${c.id === active?.id ? "active" : ""}`}
                    onClick={() => {
                      speech.stopSpeaking();
                      selectConversation(c.id);
                      setShowHistory(false);
                    }}
                  >
                    <div className="body">
                      <div className="t">{c.title}</div>
                      <div className="when">{relativeTime(c.updatedAt)}</div>
                    </div>
                    <button
                      type="button"
                      className="del"
                      title="Esborra"
                      aria-label="Esborra la conversa"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(c.id);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Chat */}
        <div ref={scrollRef} className="aip-body">
          {messages.length === 0 && !loading && (
            <div className="aip-empty">
              <span className="blob">
                <SparkleIcon size={24} />
              </span>
              <p>Demana'm sobre receptes, el menú setmanal o la llista de la compra.</p>
              <div className="aip-suggs">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="aip-sugg" onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
              {speech.recognitionSupported && (
                <span className="hint">Pots escriure o fer servir el micròfon 🎤</span>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <ChatBubble
              key={i}
              msg={m}
              synthSupported={speech.synthSupported}
              speaking={speech.speakingId === String(i)}
              onSpeak={() => speech.speak(m.text, String(i))}
              onStop={speech.stopSpeaking}
              onNavigate={onClose}
            />
          ))}
          {loading && (
            <div className="thinking">
              <span className="spin" />
              Pensant…
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="aip-foot">
          <textarea
            rows={2}
            value={input}
            disabled={loading}
            aria-label="Ordre per a la IA"
            placeholder={speech.listening ? "Escoltant…" : "Escriu la teva ordre… (Ctrl+Enter per enviar)"}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send(input);
              }
            }}
          />
          <div className="aip-ctrl">
            <LangToggle lang={lang} onChange={setLang} />
            <div style={{ flex: 1 }} />
            {speech.recognitionSupported && (
              <button
                type="button"
                onClick={toggleMic}
                disabled={loading}
                aria-label={speech.listening ? "Atura el dictat" : "Dicta per veu"}
                title={speech.listening ? "Atura el dictat" : "Dicta per veu"}
                className={`mic ${speech.listening ? "live" : ""}`}
              >
                <MicIcon />
              </button>
            )}
            <button
              type="button"
              className="aip-send"
              onClick={() => send(input)}
              disabled={loading || !input.trim() || speech.listening}
            >
              Envia
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function LangToggle({ lang, onChange }: { lang: SpeechLang; onChange: (l: SpeechLang) => void }) {
  const opts: { value: SpeechLang; label: string; title: string }[] = [
    { value: "ca-ES", label: "CA", title: "Català" },
    { value: "es-ES", label: "ES", title: "Castellà" },
  ];
  return (
    <div className="lang-toggle" role="group" aria-label="Idioma de veu">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={lang === o.value}
          title={o.title}
          className={lang === o.value ? "on" : ""}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Temps relatiu curt en català. */
function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ara mateix";
  if (min < 60) return `fa ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `fa ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return "ahir";
  if (d < 7) return `fa ${d} dies`;
  return new Date(ts).toLocaleDateString("ca-ES", {
    timeZone: "Europe/Madrid",
    day: "numeric",
    month: "short",
  });
}

function BalanceRing({ balance }: { balance: Balance }) {
  const SIZE = 30;
  const STROKE = 3;
  const R = 12;
  const CIRC = 2 * Math.PI * R;

  const total = balance.total ?? 0;
  const granted = balance.granted ?? 0;
  const toppedUp = balance.topped_up ?? 0;
  const used = granted + toppedUp - total;
  const pct = total <= 0 ? 0 : Math.max(0, Math.min(1, total / (granted + toppedUp)));
  const offset = CIRC * (1 - pct);

  const color = pct > 0.5 ? "oklch(58% 0.1 150)" : pct > 0.2 ? "#f59e0b" : "#ef4444";
  const fmt = (v: number) => `$${v.toFixed(4)}`;

  return (
    <div className="ai-ring" aria-label="Crèdits IA">
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="var(--border-strong)" strokeWidth={STROKE} />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
        />
      </svg>
      <div className="pop">
        <h5>CRÈDITS IA</h5>
        <div className="row">
          <span>Restants</span>
          <b>{fmt(total)}</b>
        </div>
        <div className="row">
          <span>Consumits</span>
          <b>{fmt(Math.max(0, used))}</b>
        </div>
        {granted > 0 && (
          <div className="row">
            <span>Bons</span>
            <b>{fmt(granted)}</b>
          </div>
        )}
        <div className="row">
          <span>Recarregats</span>
          <b>{fmt(toppedUp)}</b>
        </div>
        {!balance.available && <div className="warn">Compte no disponible</div>}
      </div>
    </div>
  );
}

/** Renderitza un subset de markdown: **negreta**, \n i llistes amb - */
function renderMarkdown(text: string): React.ReactNode {
  return text.split("\n").map((line, i) => {
    const isBullet = /^(\s*[-*])\s+/.test(line);
    const content = line.replace(/^(\s*[-*])\s+/, "");
    const parts = (isBullet ? content : line).split(/\*\*([^*]+)\*\*/g);
    const rich = parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{p}</strong> : p));
    return (
      <span key={i} className={isBullet ? "li" : "block"}>
        {rich}
      </span>
    );
  });
}

function ChatBubble({
  msg,
  synthSupported,
  speaking,
  onSpeak,
  onStop,
  onNavigate,
}: {
  msg: ChatMsg;
  synthSupported: boolean;
  speaking: boolean;
  onSpeak: () => void;
  onStop: () => void;
  onNavigate: () => void;
}) {
  if (msg.role === "user") {
    return <div className="bubble user">{msg.text}</div>;
  }
  if (msg.role === "error") {
    return <div className="bubble error">{renderMarkdown(msg.text)}</div>;
  }
  return (
    <div className="botwrap">
      <div className="bubble bot">{renderMarkdown(msg.text)}</div>
      {msg.actions && msg.actions.length > 0 && (
        <div className="msg-tools">
          {msg.actions.map((a, i) => {
            const failed = "error" in a.result;
            const label = TOOL_LABELS[a.tool] ?? a.tool;
            // La recepta creada enllaça a la seva pàgina (el resultat porta l'id).
            if (!failed && a.tool === "create_recipe" && a.result.id != null) {
              return (
                <Link key={i} className="tool-tag" to={`/receptes/${a.result.id}`} onClick={onNavigate}>
                  {label}
                </Link>
              );
            }
            return (
              <span
                key={i}
                className={`tool-tag ${failed ? "fail" : ""}`}
                title={failed ? String(a.result.error) : undefined}
              >
                {failed ? "⚠ " : ""}
                {label}
              </span>
            );
          })}
        </div>
      )}
      {synthSupported && (
        <button type="button" className={`tts ${speaking ? "on" : ""}`} onClick={speaking ? onStop : onSpeak}>
          {speaking ? <StopIcon /> : <SpeakerIcon />}
          {speaking ? "Atura" : "Escolta"}
        </button>
      )}
    </div>
  );
}

// --- Icones ------------------------------------------------------------------

function SparkleIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.9 15.5A2 2 0 0 0 8.5 14.1l-6.1-1.6a.5.5 0 0 1 0-1l6.1-1.6A2 2 0 0 0 9.9 8.5l1.6-6.1a.5.5 0 0 1 1 0l1.6 6.1A2 2 0 0 0 15.5 9.9l6.1 1.6a.5.5 0 0 1 0 1l-6.1 1.6a2 2 0 0 0-1.4 1.4l-1.6 6.1a.5.5 0 0 1-1 0z" />
      <path d="M20 3v4M22 5h-4M4 17v2M5 18H3" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function MaximizeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M6 6l1 14h10l1-14" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}
